/**
 * Completion celebration (WR-009).
 *
 * A hand-rolled canvas confetti burst — no new dependency. `canvas-confetti`
 * was pre-approved by the WR but buys nothing here: the burst is ~90 lines,
 * and rolling it ourselves is what lets the particles take their colours from
 * the active theme's CSS variables at fire time, which a fixed palette in a
 * third-party lib cannot do across light / dark / Catppuccin.
 *
 * Imperative on purpose. Completions happen inside async submit handlers
 * (`handleDragEnd`, `handleSave`), so the natural call site is a function call
 * after the API resolves, not a piece of render state. React never needs to
 * know the canvas exists.
 *
 * Usage:
 *   import { celebrate, popElement } from './Celebration';
 *   celebrate(cardEl.getBoundingClientRect());
 *   popElement(cardEl);
 */

import './Celebration.css';

const PARTICLE_COUNT   = 60;     // per burst, within the WR's 40–80 band
const COALESCE_COUNT   = 25;     // added when bursts collapse into one
const MAX_PARTICLES    = 140;    // ceiling so a mash of completions stays cheap
const THROTTLE_MS      = 800;
const LIFETIME_MS      = 1400;   // ≤ ~1.5s per the WR
const GRAVITY          = 0.00075; // px per ms²

// Theme tokens the confetti draws from, best-looking first.
const COLOR_VARS = [
  '--accent',
  '--accent-light',
  '--secondary',
  '--secondary-light',
  '--status-done',
];

// Last-resort palette, used only if the theme yields nothing the canvas can
// parse. Mirrors the default theme's accent / brass / sage.
const FALLBACK_COLORS = ['#C8442F', '#DD6450', '#D49652', '#89A37B'];

let burst = null;        // { canvas, ctx, particles, raf, dpr }
// -Infinity, not 0: performance.now() is near zero just after page load, and
// a 0 seed would swallow a celebration in the first 800ms of a session.
let lastFireAt = -Infinity;

function prefersReducedMotion() {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Resolve the theme's colours, dropping any the canvas cannot parse.
 *
 * The tokens are authored as `oklch(...)`, which every browser that ships
 * oklch handles as a fillStyle — but rather than assume, each value is
 * probed against a throwaway context and discarded if it does not take.
 * Falls back to FALLBACK_COLORS if the theme yields nothing usable.
 *
 * The tokens resolve through aliases (`--accent: var(--eu)`); custom
 * properties substitute var() at computed-value time, so what comes back
 * here is the final colour, not the alias.
 */
function themeColors() {
  const styles = getComputedStyle(document.documentElement);
  const probe = document.createElement('canvas').getContext('2d');
  const colors = [];

  for (const name of COLOR_VARS) {
    const value = styles.getPropertyValue(name).trim();
    if (!value) continue;
    probe.fillStyle = '#000000';
    probe.fillStyle = value;
    // An unparseable value leaves fillStyle at the sentinel.
    if (probe.fillStyle !== '#000000') colors.push(value);
  }

  return colors.length > 0 ? colors : FALLBACK_COLORS;
}

/** Where the burst starts: a DOMRect's centre, an {x,y}, or the viewport centre. */
function resolveOrigin(origin) {
  if (origin && typeof origin.x === 'number' && typeof origin.y === 'number' && !('width' in origin)) {
    return { x: origin.x, y: origin.y };
  }
  if (origin && typeof origin.width === 'number') {
    return { x: origin.left + origin.width / 2, y: origin.top + origin.height / 2 };
  }
  return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
}

function makeParticles(origin, colors, count) {
  const now = performance.now();
  const particles = [];

  for (let i = 0; i < count; i++) {
    // Upward-biased fan: angle spread over ~200°, centred on straight up.
    const angle = (-Math.PI / 2) + (Math.random() - 0.5) * (Math.PI * 1.1);
    const speed = 0.25 + Math.random() * 0.45;   // px/ms
    particles.push({
      x: origin.x,
      y: origin.y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      size: 5 + Math.random() * 5,
      rotation: Math.random() * Math.PI,
      spin: (Math.random() - 0.5) * 0.012,
      color: colors[i % colors.length],
      bornAt: now,
      life: LIFETIME_MS * (0.65 + Math.random() * 0.35),
    });
  }

  return particles;
}

function createCanvas() {
  const dpr = window.devicePixelRatio || 1;
  const canvas = document.createElement('canvas');

  canvas.width  = window.innerWidth * dpr;
  canvas.height = window.innerHeight * dpr;
  Object.assign(canvas.style, {
    position: 'fixed',
    inset: '0',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',   // never intercepts clicks or a drag in flight
    zIndex: '9999',
  });
  canvas.setAttribute('aria-hidden', 'true');

  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);

  return { canvas, ctx, dpr };
}

function teardown() {
  if (!burst) return;
  cancelAnimationFrame(burst.raf);
  burst.canvas.remove();
  burst = null;
}

function frame(now) {
  if (!burst) return;

  const { ctx, particles } = burst;
  ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

  let alive = 0;
  let previous = burst.lastFrameAt ?? now;
  const dt = Math.min(now - previous, 48);   // clamp: a backgrounded tab must not teleport particles
  burst.lastFrameAt = now;

  for (const p of particles) {
    const age = now - p.bornAt;
    if (age >= p.life) continue;
    alive++;

    p.vy += GRAVITY * dt;
    p.x  += p.vx * dt;
    p.y  += p.vy * dt;
    p.rotation += p.spin * dt;

    ctx.save();
    ctx.globalAlpha = 1 - age / p.life;          // fade out
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rotation);
    ctx.fillStyle = p.color;
    ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
    ctx.restore();
  }

  if (alive === 0) {
    teardown();      // canvas out of the DOM, rAF loop stopped — zero idle cost
    return;
  }

  burst.raf = requestAnimationFrame(frame);
}

/**
 * Brief, static acknowledgment for `prefers-reduced-motion` users — the
 * feedback still lands, it just doesn't move.
 */
function reducedAcknowledgment(origin) {
  const { x, y } = resolveOrigin(origin);
  const mark = document.createElement('div');

  mark.className = 'celebration-check';
  mark.setAttribute('aria-hidden', 'true');
  mark.textContent = '✓';
  Object.assign(mark.style, {
    position: 'fixed',
    left: `${x}px`,
    top: `${y}px`,
    zIndex: '9999',
    pointerEvents: 'none',
  });

  document.body.appendChild(mark);
  setTimeout(() => mark.remove(), 900);
}

/**
 * Fire a celebration at `origin` (a DOMRect, an {x, y}, or nothing for the
 * viewport centre).
 *
 * Call this only on a genuine status transition into done that this client
 * initiated and the server accepted — never from render, an effect, or a
 * refetch.
 */
export function celebrate(origin) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  if (prefersReducedMotion()) {
    reducedAcknowledgment(origin);
    return;
  }

  const now = performance.now();
  const point = resolveOrigin(origin);
  const colors = themeColors();

  // Inside the throttle window, completions coalesce into the burst already
  // running (a bit bigger, from the new origin) rather than stacking canvases.
  if (now - lastFireAt < THROTTLE_MS) {
    if (burst && burst.particles.length < MAX_PARTICLES) {
      burst.particles.push(...makeParticles(point, colors, COALESCE_COUNT));
    }
    return;
  }

  lastFireAt = now;

  if (!burst) {
    const { canvas, ctx, dpr } = createCanvas();
    burst = { canvas, ctx, dpr, particles: [], raf: 0, lastFrameAt: null };
    burst.raf = requestAnimationFrame(frame);
  }

  burst.particles.push(...makeParticles(point, colors, PARTICLE_COUNT));
}

/**
 * The companion scale-pop on the card or control that was completed.
 * No-op under reduced motion, where the checkmark carries the feedback.
 */
export function popElement(el) {
  if (!el || prefersReducedMotion()) return;
  el.classList.remove('celebration-pop');
  // Force a reflow so re-adding the class restarts the animation when the
  // same card is completed twice in a row.
  void el.offsetWidth;
  el.classList.add('celebration-pop');
  setTimeout(() => el.classList.remove('celebration-pop'), 400);
}
